import { Filter, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

interface MultiSelectFilterProps {
  label: string
  selectedValues: string[]
  options: string[]
  onChange: (values: string[]) => void
  /** Color theme variant */
  variant?: 'primary' | 'violet' | 'emerald'
}

/**
 * Reusable multi-select filter dropdown with search.
 * Used for filtering by approach strategies and study plans.
 */
export function MultiSelectFilter({
  label,
  selectedValues,
  options,
  onChange,
  variant = 'primary',
}: MultiSelectFilterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Color classes based on variant
  const colorClasses = {
    primary: {
      button:
        'border-primary/60 bg-primary/10 text-primary dark:border-primary/40 dark:bg-primary/20 dark:text-primary/90',
      clearHover: 'hover:bg-primary/20',
      badge: 'bg-primary/20 text-primary',
      badgeHover: 'hover:bg-primary/30',
      selected: 'bg-primary/10 text-primary',
      checkbox: 'border-primary bg-primary text-primary-foreground',
    },
    violet: {
      button:
        'border-violet-500/60 bg-violet-500/10 text-violet-400 dark:border-violet-500/40 dark:bg-violet-500/20',
      clearHover: 'hover:bg-violet-500/20',
      badge: 'bg-violet-500/20 text-violet-400',
      badgeHover: 'hover:bg-violet-500/30',
      selected: 'bg-violet-500/10 text-violet-400',
      checkbox: 'border-violet-500 bg-violet-500 text-white',
    },
    emerald: {
      button:
        'border-emerald-500/60 bg-emerald-500/10 text-emerald-400 dark:border-emerald-500/40 dark:bg-emerald-500/20',
      clearHover: 'hover:bg-emerald-500/20',
      badge: 'bg-emerald-500/20 text-emerald-400',
      badgeHover: 'hover:bg-emerald-500/30',
      selected: 'bg-emerald-500/10 text-emerald-400',
      checkbox: 'border-emerald-500 bg-emerald-500 text-white',
    },
  }

  const colors = colorClasses[variant]

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options
    const query = searchQuery.toLowerCase()
    return options.filter((opt) => opt.toLowerCase().includes(query))
  }, [options, searchQuery])

  const handleToggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value))
    } else {
      onChange([...selectedValues, value])
    }
  }

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
    setSearchQuery('')
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
          selectedValues.length > 0
            ? colors.button
            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <Filter size={14} />
        {selectedValues.length === 0
          ? label
          : `${selectedValues.length} ${label.toLowerCase().replace(/s$/, '')}${selectedValues.length > 1 ? 's' : ''}`}
        {selectedValues.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClearAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ')
                handleClearAll(e as unknown as React.MouseEvent)
            }}
            className={`-mr-1 ml-1 rounded p-0.5 ${colors.clearHover}`}
          >
            <X size={12} />
          </span>
        )}
      </button>

      {isOpen && (
        <div className="bg-popover border-border absolute top-full left-0 z-30 mt-1 min-w-[240px] rounded-lg border shadow-xl">
          {/* Search input */}
          <div className="border-border border-b p-2">
            <div className="relative">
              <Search
                size={14}
                className="text-muted-foreground absolute top-1/2 left-2 -translate-y-1/2"
              />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="border-border bg-muted w-full rounded border py-1.5 pr-2 pl-7 text-sm"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Selected values */}
          {selectedValues.length > 0 && (
            <div className="border-border flex flex-wrap gap-1 border-b p-2">
              {selectedValues.map((value) => (
                <span
                  key={value}
                  className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs ${colors.badge}`}
                >
                  {value}
                  <button
                    type="button"
                    onClick={() => handleToggleValue(value)}
                    className={`rounded ${colors.badgeHover}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Options list */}
          <div className="max-h-[250px] overflow-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="text-muted-foreground px-3 py-2 text-sm">
                No {label.toLowerCase()} found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option)
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleToggleValue(option)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
                      isSelected
                        ? colors.selected
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <div
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        isSelected ? colors.checkbox : 'border-border'
                      }`}
                    >
                      {isSelected && (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                        >
                          <path
                            d="M8.5 2.5L3.5 7.5L1.5 5.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="truncate">{option}</span>
                  </button>
                )
              })
            )}
          </div>

          {/* Clear all button */}
          {selectedValues.length > 0 && (
            <>
              <div className="border-border border-t" />
              <button
                type="button"
                onClick={handleClearAll}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
              >
                <X size={14} />
                Clear all
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
